import { PaymentElement, useCheckout } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { cn } from '@/utils/cn';

const validateEmail = async (email: string, checkout: any) => {
  const updateResult = await checkout.updateEmail(email);
  const isValid = updateResult.type !== 'error';

  return { isValid, message: !isValid ? updateResult.error.message : null };
};

const EmailInput = ({
  email,
  setEmail,
  error,
  setError,
}: {
  email: string;
  setEmail: (email: string) => void;
  error: string | null;
  setError: (error: null) => void;
}) => {
  const checkout = useCheckout();

  const handleBlur = async () => {
    if (!email) {
      return;
    }

    const { isValid, message } = await validateEmail(email, checkout);
    if (!isValid) {
      setError(message);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setEmail(e.target.value);
  };

  return (
    <>
      <label className='flex flex-col gap-1'>
        <p className='text-sub3'>Email</p>
        <input
          id='email'
          type='text'
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder='you@example.com'
          className='rounded-s border border-grey-010 p-4 shadow-sm'
        />
      </label>
    </>
  );
};

const CheckoutForm = ({
  onSubmit,
  isSure,
  time,
  price,
}: {
  onSubmit: () => Promise<void>;
  isSure: boolean;
  time: string;
  price: number;
}) => {
  const checkout = useCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await onSubmit();
    } finally {
      setIsLoading(false);
      setIsPaid(true);
    }
  };

  return (
    <div className='flex items-end gap-1'>
      <div
        className={cn(
          'flex w-[700px] flex-col gap-5 rounded-s bg-grey-000 p-5',
          isSure && 'w-[calc(100vw-60px)]',
        )}
      >
        <EmailInput
          email={email}
          setEmail={setEmail}
          error={emailError}
          setError={setEmailError}
        />
        <h4 className='text-sub3'>Payment</h4>
        <PaymentElement id='payment-element' />
        <button
          className='w-full rounded-s bg-[#0570DE] py-2 text-grey-000 disabled:bg-grey-010 disabled:text-grey-050'
          onClick={handleSubmit}
          disabled={isPaid}
        >
          {isLoading ? (
            <div className='flex items-center justify-center'>
              <div className='size-5 animate-spin rounded-full border-2 border-grey-000 border-t-transparent'></div>
              <span id='button-text' className='ml-2'>
                {'Loading'}
              </span>
            </div>
          ) : isPaid ? (
            <span id='button-text'>{'✅ Paid'}</span>
          ) : (
            <span id='button-text'>{`Pay $${price.toLocaleString()} now`}</span>
          )}
        </button>
      </div>
      <p className='text-caption2 tracking-l text-grey-050'>{time}</p>
    </div>
  );
};

export default CheckoutForm;
